<script setup>
import { computed } from 'vue'

const props = defineProps({
  // 0..1 — how far the mark has filled (bottom-up), follows load progress
  progress: {
    type: Number,
    default: 0,
  },
  message: {
    type: String,
    default: '',
  },
})

const p = computed(() => Math.min(Math.max(props.progress, 0), 1))

// Reveal the white fill from the bottom: clip away the top (1 - progress).
const fillClip = computed(() => `inset(${(1 - p.value) * 100}% 0 0 0)`)

// The actual Webscout mark — src/assets/ws_logo_idea.svg (W-in-circle).
const W_PATH =
  'M63.5703 127C98.6404 127 127.07 98.5701 127.07 63.5C127.07 28.4299 98.6404 0 63.5703 0C50.7839 0 38.8802 3.77918 28.9152 10.2815L49.7577 67.6917L60.5783 27.7908H63.1411L76.9516 67.6917L87.7723 27.7908L102.437 28.2565L83.9281 99.6746L80.5111 113.027L76.5245 99.8299H72.3956L63.1411 73.281L56.0223 99.8299H52.1781L49.0458 112.406L16.8575 20.486C6.43551 31.7984 0.0703125 46.9059 0.0703125 63.5C0.0703125 98.5701 28.5002 127 63.5703 127Z'
</script>

<template>
  <div class="boot-splash">
    <div class="boot-inner">
      <svg
        class="boot-logo"
        width="76"
        height="75"
        viewBox="0 0 128 127"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden="true"
      >
        <!-- Dim silhouette -->
        <path :d="W_PATH" class="boot-track" />
        <!-- White fill, revealed bottom-up by progress -->
        <path :d="W_PATH" class="boot-fill" :style="{ clipPath: fillClip }" />
      </svg>

      <transition name="boot-msg" mode="out-in">
        <span class="boot-message" :key="message">{{ message }}</span>
      </transition>
    </div>
  </div>
</template>

<style scoped>
.boot-splash {
  position: absolute;
  inset: 0;
  z-index: 50;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: var(--surface-card);
}

.boot-inner {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1.75rem;
}

.boot-logo {
  display: block;
}

.boot-track {
  fill: var(--surface-control);
}

.boot-fill {
  fill: #ffffff;
}

.boot-message {
  color: var(--text-primary);
  font-size: var(--fs-sm);
  font-family: var(--font-body);
  letter-spacing: 0.01em;
}

/* Message cross-fade */
.boot-msg-enter-active,
.boot-msg-leave-active {
  transition: opacity 0.18s ease;
}
.boot-msg-enter-from,
.boot-msg-leave-to {
  opacity: 0;
}

@media (prefers-reduced-motion: no-preference) {
  /* Smoothly raise the fill between progress steps */
  .boot-fill {
    transition: clip-path 0.45s ease;
  }
}
</style>
